import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { listBscPractices, getBscCount, getBscSuggestions, checkNewBsc, markAllBscPracticesRead } from "../../api/client";
import { StatusIcon } from "../../lib/status";
import { LockChip } from "../../lib/LockChip";
import { ClientSourceChip, CLIENT_SOURCE_OPTIONS } from "../../lib/ClientSourceChip";
import { HanjaBadges } from "../../lib/hanja";
import type { BscPractice, BscStatus, BscSuggestion, BscSortKey, BscCount } from "../../api/types";
import { toCountryIso2 } from "../../helpers/countryCode";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100];
const REFRESH_INTERVAL = 30_000;

// "Tutte" in fondo (ultimo filtro, a destra) su richiesta cliente; il default
// all'apertura resta "Da rivedere" (vedi `status` letto dall'URL con fallback
// pending_review). "all" -> nessun filtro di stato (undefined verso il BE).
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "pending_review", label: "Da rivedere" },
  { value: "pending_documents", label: "Doc. mancanti" },
  { value: "approved", label: "Approvate" },
  { value: "rejected", label: "Rifiutate" },
  { value: "discarded", label: "Scartate" },
  { value: "error", label: "Errore" },
  { value: "all", label: "Tutte" },
];

// Resa stato unificata → lib/status
function getStatusIcon(s: string) {
  return <StatusIcon status={s} />;
}

function formatDate(iso: string) {
  try {
    const d = new Date(iso);
    const dayMonth = d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
    const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    return `${dayMonth} ${time}`;
  } catch { return iso; }
}

function formatDateFull(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// ── Column header con sort + filter popover ──────────────────────────────
// IMPORTANTE: componente top-level (NON definito dentro BscList). Se fosse
// annidato nel corpo di BscList, ad ogni render del genitore (anche solo il
// tick "N secondi fa" ogni 1s) React gli assegnerebbe una nuova identità di
// funzione e smonterebbe/rimonterebbe l'intero sottoalbero — incluso
// l'<input> del filtro testo, che essendo non controllato (defaultValue)
// perderebbe qualunque testo digitato dall'operatore ad ogni render.
const FILTER_KEYS = [
  "q", "date_from", "date_to", "bl", "invoice", "country",
  "client", "origin", "dox", "hanja", "cert", "route", "party",
] as const;

function ColHeader({
  label, sortKey, filterKey, filterType, filterValue, options, style,
  sort, openFilter, setOpenFilter, setFilter, setTextFilter,
}: {
  label: string; sortKey: string; filterKey?: string; filterType?: "text" | "select";
  filterValue?: string; options?: { value: string; label: string }[]; style?: React.CSSProperties;
  sort: string;
  openFilter: string | null;
  setOpenFilter: (key: string | null) => void;
  setFilter: (updates: Record<string, string | null>) => void;
  setTextFilter: (key: string, value: string) => void;
}) {
  const isAsc = sort === `${sortKey}_asc`;
  const isDesc = sort === `${sortKey}_desc`;
  const isSortActive = isAsc || isDesc;
  const nextSort = isAsc ? `${sortKey}_desc` : isDesc ? null : `${sortKey}_asc`;
  const isFiltered = filterKey ? Boolean(filterValue) : false;
  const isOpen = openFilter === sortKey;

  return (
    <th style={{ position: "relative", ...style }}>
      <div style={{ display: "flex", alignItems: "center", gap: 2, userSelect: "none" }}>
        <span style={{ cursor: "pointer", flex: 1 }} onClick={() => setFilter({ sort: nextSort })}>
          {label}
        </span>
        <span className={`material-symbols-outlined${isSortActive ? "" : " th-icon-hover"}`}
          onClick={() => setFilter({ sort: nextSort })}
          style={{ fontSize: 14, cursor: "pointer", padding: 2,
                   color: "var(--indaco-brand-primary)", opacity: isSortActive ? 1 : 0.6 }}>
          {isAsc ? "arrow_upward" : isDesc ? "arrow_downward" : "swap_vert"}
        </span>
        {filterKey && (
          <span className={`material-symbols-outlined${isFiltered ? "" : " th-icon-hover"}`}
            data-filter-toggle onClick={() => setOpenFilter(isOpen ? null : sortKey)}
            style={{ fontSize: 14, cursor: "pointer", padding: 2,
                     color: "var(--indaco-brand-primary)", opacity: isFiltered ? 1 : 0.6,
                     background: isFiltered ? "rgba(var(--indaco-brand-primary-rgb),0.1)" : "transparent",
                     borderRadius: 4 }}>
            filter_alt
          </span>
        )}
      </div>
      {isOpen && filterKey && (
        <div data-filter-popover
          style={{ position: "absolute", top: "100%", left: 0, zIndex: 100, minWidth: 180,
                   background: "var(--indaco-white)", border: "1px solid var(--indaco-gray-200)",
                   borderRadius: "var(--border-radius-md)", boxShadow: "var(--shadow-lg)",
                   padding: "var(--space-2) var(--space-3)", marginTop: 4 }}>
          {filterType === "select" && options && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {options.map((o) => (
                <button key={o.value}
                  className={`btn btn-sm ${filterValue === o.value || (!filterValue && !o.value) ? "btn-primary" : "btn-ghost"}`}
                  style={{ justifyContent: "flex-start", fontSize: "var(--font-size-xs)", padding: "var(--space-1) var(--space-2)" }}
                  onClick={() => { setFilter({ [filterKey]: o.value || null }); setOpenFilter(null); }}>
                  {o.label}
                </button>
              ))}
            </div>
          )}
          {filterType === "text" && (
            <input autoFocus type="text" className="form-input"
              style={{ fontSize: "var(--font-size-xs)", padding: "var(--space-1) var(--space-2)", width: "100%" }}
              placeholder={`Filtra ${label.toLowerCase()}...`}
              defaultValue={filterValue}
              onChange={(e) => setTextFilter(filterKey, e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") setOpenFilter(null); }} />
          )}
          {filterValue && (
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 6, fontSize: "var(--font-size-xs)", width: "100%", color: "var(--indaco-danger)" }}
              onClick={() => { setFilter({ [filterKey]: null }); setOpenFilter(null); }}>
              <span className="material-symbols-outlined" style={{ fontSize: 13, marginRight: 4 }}>close</span>Rimuovi filtro
            </button>
          )}
        </div>
      )}
    </th>
  );
}

export function BscList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Passato come location.state a BscDetail cosi' il bottone "Indietro" torna
  // esattamente al filtro/pagina/ricerca da cui si e' partiti, invece di
  // ripiombare sempre sul default (pending_review) con navigate("/bsc") secco.
  const goToPractice = (id: string) => navigate(`/bsc/${id}`, { state: { fromQuery: location.search } });

  // ── Lettura filtri da URL ──
  const status = searchParams.get("status") || "pending_review";
  const fQ = searchParams.get("q") || "";
  const fBl = searchParams.get("bl") || "";
  const fInvoice = searchParams.get("invoice") || "";
  const fCountry = searchParams.get("country") || "";
  const fOrigin = searchParams.get("origin") || "";
  const fClient = searchParams.get("client") || "";
  const fDox = searchParams.get("dox") || "";
  const fHanja = searchParams.get("hanja") || "";
  const fCert = searchParams.get("cert") || "";
  const fRoute = searchParams.get("route") || "";
  const fParty = searchParams.get("party") || "";
  const sort = searchParams.get("sort") || "date_desc";
  const page = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = parseInt(searchParams.get("size") || String(DEFAULT_PAGE_SIZE), 10);
  const dateFrom = searchParams.get("date_from") || "";
  const dateTo = searchParams.get("date_to") || "";

  const [data, setData] = useState<{ items: BscPractice[]; total: number } | null>(null);
  const [counts, setCounts] = useState<BscCount | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState<string | null>(null);
  const [markingStatus, setMarkingStatus] = useState<string | null>(null);
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // ── Search bar state ──
  const [searchText, setSearchText] = useState(fQ);
  const [suggestions, setSuggestions] = useState<BscSuggestion[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  // suggestIdx: selezione da TASTIERA (frecce), unica sorgente di verita' per cosa fa
  // Invio. hoverIdx: solo evidenziazione visiva del mouse, non deve mai influenzare
  // Invio -- altrimenti passare il mouse sul primo suggerimento (facile, e' proprio
  // sotto la barra di ricerca) dirotta silenziosamente Invio verso quel risultato
  // invece di eseguire "cerca in tutti i risultati" come promette l'hint sotto la lista.
  const [suggestIdx, setSuggestIdx] = useState(-1);
  const [hoverIdx, setHoverIdx] = useState(-1);
  // Controlla la visibilita' del dropdown indipendentemente da "ci sono
  // suggerimenti?": deve restare visibile anche a 0 suggerimenti per mostrare
  // l'hint "Invio cerca in tutti i campi" (vedi commento piu' sotto).
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const suggestTimer = useRef<ReturnType<typeof setTimeout>>();
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [lastVisitedId, setLastVisitedId] = useState<string | null>(() => localStorage.getItem("lastVisitedBscId"));

  useEffect(() => { setSearchText(fQ); }, [fQ]);

  // Close search dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (searchContainerRef.current?.contains(e.target as Node)) return;
      setDropdownOpen(false);
    }
    const t = setTimeout(() => document.addEventListener("mousedown", handleClick), 0);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", handleClick); };
  }, [dropdownOpen]);

  function handleSearchInput(val: string) {
    setSearchText(val);
    setSuggestIdx(-1);
    setHoverIdx(-1);
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (val.length < 2) { setSuggestions([]); setDropdownOpen(false); return; }
    setDropdownOpen(true);
    setSuggestLoading(true);
    suggestTimer.current = setTimeout(() => {
      getBscSuggestions(val)
        .then(r => setSuggestions(r))
        .catch(() => setSuggestions([]))
        .finally(() => setSuggestLoading(false));
    }, 300);
  }

  function applySearch(q: string) {
    setSuggestions([]);
    setDropdownOpen(false);
    setFilter({ q: q.trim() || null });
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { setDropdownOpen(false); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setSuggestIdx(i => Math.min(i + 1, suggestions.length - 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setSuggestIdx(i => Math.max(i - 1, -1)); return; }
    if (e.key === "Enter") {
      e.preventDefault();
      // suggestIdx e' impostato SOLO da ArrowUp/ArrowDown (mai dal mouse, vedi
      // hoverIdx sopra): Invio senza navigazione da tastiera cerca sempre su
      // tutti i campi, mai una navigazione "a sorpresa" verso un suggerimento
      // su cui il mouse era semplicemente appoggiato.
      if (suggestIdx >= 0 && suggestions[suggestIdx]) {
        goToPractice(suggestions[suggestIdx].id);
        setSuggestions([]);
        setDropdownOpen(false);
      } else {
        applySearch(searchText);
      }
    }
  }

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!openFilter) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (target.closest("[data-filter-popover]") || target.closest("[data-filter-toggle]")) return;
      setOpenFilter(null);
    }
    const timer = setTimeout(() => document.addEventListener("click", handleClick), 0);
    return () => { clearTimeout(timer); document.removeEventListener("click", handleClick); };
  }, [openFilter]);

  function setFilter(updates: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === "" || v === undefined) next.delete(k);
        else next.set(k, v);
      }
      if (!("page" in updates)) next.delete("page");
      return next;
    }, { replace: true });
  }

  function setTextFilter(key: string, value: string) {
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(() => setFilter({ [key]: value || null }), 300);
  }

  const loadCounts = useCallback(() => {
    getBscCount()
      .then((c) => setCounts(c))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setIsLoading(true);
    listBscPractices({
      status: (status === "all" || !status) ? undefined : status,
      q: fQ || undefined,
      bl: fBl || undefined,
      invoice: fInvoice || undefined,
      country: fCountry || undefined,
      client: fClient || undefined,
      dox: fDox || undefined,
      hanja: fHanja || undefined,
      cert: fCert || undefined,
      route: fRoute || undefined,
      party: fParty || undefined,
      origin: fOrigin || undefined,
      sort: sort as BscSortKey,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      page,
      page_size: pageSize,
    })
      .then((d) => { setData({ items: d.items, total: d.total }); setError(null); setLastRefresh(Date.now()); })
      .catch((e) => setError(e instanceof Error ? e.message : "Errore caricamento"))
      .finally(() => setIsLoading(false));
  }, [status, fQ, fBl, fInvoice, fCountry, fClient, fOrigin, fDox, fHanja, fCert, fRoute, fParty,
      sort, dateFrom, dateTo, page, pageSize]);

  // "Forza aggiornamento": forza subito l'ingestione della mailbox BSC (come il
  // tasto ricevi di un client email), poi ricarica lista + contatori.
  const handleCheckNew = useCallback(async () => {
    setChecking(true);
    setCheckMsg(null);
    try {
      const r = await checkNewBsc();
      setCheckMsg(r.error ? `Errore: ${r.error}` : r.new > 0 ? `${r.new} nuove pratiche` : "Nessuna nuova email");
      load();
      loadCounts();
    } catch {
      setCheckMsg("Errore nel controllo");
    } finally {
      setChecking(false);
      setTimeout(() => setCheckMsg(null), 5000);
    }
  }, [load, loadCounts]);

  // Bottone "segna tutte come lette" su ogni pill — bulk sull'endpoint
  // /mark-all-read (stesso stato condiviso di team del mark-read singolo).
  const handleMarkAllRead = useCallback(async (statusValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setMarkingStatus(statusValue);
    try {
      await markAllBscPracticesRead(statusValue);
      load();
      loadCounts();
    } catch {
      setCheckMsg("Errore nel segnare come lette");
      setTimeout(() => setCheckMsg(null), 5000);
    } finally {
      setMarkingStatus(null);
    }
  }, [load, loadCounts]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => {
    const id = setInterval(() => { load(); loadCounts(); }, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [load, loadCounts]);
  useEffect(() => {
    const id = setInterval(() => setSecondsAgo(Math.floor((Date.now() - lastRefresh) / 1000)), 1000);
    return () => clearInterval(id);
  }, [lastRefresh]);

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;
  // Tutte le chiavi filtro in un posto solo: "c'e' un filtro attivo?" e il
  // pulsante "pulisci" leggono da qui. Prima erano due elenchi scritti a mano e
  // il secondo era rimasto indietro: i filtri nuovi non venivano azzerati, la
  // lista restava vuota e l'unico modo di uscirne era ricaricare la pagina.
  const hasAnyFilter = FILTER_KEYS.some((k) => !!searchParams.get(k));
  const showDropdown = dropdownOpen && searchText.length >= 2;

  const title = "Pratiche BSC";

  return (
    <div style={{ padding: "var(--space-4)" }}>
      {/* Header */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-3)", gap: "var(--space-3)", rowGap: "var(--space-2)" }}>
        <h1 className="page-title" style={{ flexShrink: 0 }}>{title}</h1>

        {/* ── Search bar ── */}
        <div ref={searchContainerRef} style={{ position: "relative", flex: 1, maxWidth: 480, minWidth: 0 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            border: fQ ? "1.5px solid var(--indaco-brand-primary)" : "1px solid var(--indaco-gray-300)",
            borderRadius: "var(--border-radius-md)", background: fQ ? "var(--indaco-brand-primary-light)" : "var(--indaco-white)",
            padding: "5px 12px",
            boxShadow: showDropdown ? "var(--shadow-sm)" : "none",
            transition: "border-color .15s, background .15s, box-shadow .15s",
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: fQ ? "var(--indaco-brand-primary)" : "var(--indaco-gray-400)", flexShrink: 0 }}>search</span>
            <input type="text" value={searchText}
              placeholder="Cerca: cliente, BL, invoice, nave, pod, exporter, importer, container, merce..."
              onChange={e => handleSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => { if (searchText.length >= 2) setDropdownOpen(true); }}
              style={{ flex: 1, border: "none", outline: "none", background: "transparent",
                       fontSize: "var(--font-size-sm)", padding: "3px 0", minWidth: 0 }} />
            {suggestLoading && <span className="spinner" style={{ width: 14, height: 14, flexShrink: 0 }} />}
            {(searchText || fQ) && (
              <button onClick={() => { setSearchText(""); applySearch(""); }}
                style={{ display: "flex", alignItems: "center", border: "none", background: "none", cursor: "pointer", padding: 2, flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-400)" }}>close</span>
              </button>
            )}
          </div>
          {/* Suggestions dropdown. L'hint "Invio per cercare" e il footer vanno
              mostrati SEMPRE (anche a 0 suggerimenti): l'autocomplete copre solo
              6 campi rapidi (BL/invoice/nave/pod/importer/exporter), ma Invio
              cerca su TUTTI i campi estratti (bsc_data completo). Senza questo,
              cercando un termine presente solo in un campo non coperto
              dall'autocomplete (es. container, merce, indirizzi) l'utente non
              vedeva alcun hint e non sapeva che Invio avrebbe comunque funzionato. */}
          {showDropdown && (
            <div style={{
              position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 200,
              background: "var(--indaco-white)", border: "1px solid var(--indaco-gray-200)",
              borderRadius: "var(--border-radius-md)", boxShadow: "var(--shadow-lg)",
              overflow: "hidden", maxHeight: 360, overflowY: "auto",
            }}>
              {suggestions.map((s, i) => (
                <div key={s.id}
                  onMouseDown={() => { goToPractice(s.id); setSuggestions([]); }}
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx(-1)}
                  style={{
                    padding: "var(--space-2) var(--space-3)", cursor: "pointer", fontSize: "var(--font-size-sm)",
                    background: (i === suggestIdx || i === hoverIdx) ? "var(--indaco-gray-50)" : "transparent",
                    borderBottom: "1px solid var(--indaco-gray-100)",
                  }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: "var(--font-weight-semibold)", fontSize: 11, color: "var(--indaco-gray-400)", fontFamily: "monospace", flexShrink: 0 }}>
                      {s.id.slice(0, 8)}
                    </span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                      {s.bl_number || "(senza BL)"}
                    </span>
                    {s.pod && (
                      <code style={{ fontSize: 10, color: "var(--indaco-gray-500)", flexShrink: 0 }}>→ {s.pod}</code>
                    )}
                  </div>
                  {(s.client_name || s.vessel_name || s.exporter_name || s.importer_name) && (
                    <div style={{ fontSize: 11, color: "var(--indaco-gray-400)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {[s.client_name, s.vessel_name, s.exporter_name, s.importer_name].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
              ))}
              {suggestions.length === 0 && !suggestLoading && (
                <div style={{ padding: "var(--space-2) var(--space-3)", fontSize: 11, color: "var(--indaco-gray-400)" }}>
                  Nessun suggerimento rapido (BL/invoice/nave/pod/importer/exporter) per questo termine.
                </div>
              )}
              <div style={{ padding: "var(--space-2) var(--space-3)", fontSize: 11, color: "var(--indaco-brand-primary)", background: "var(--indaco-gray-50)", textAlign: "center", fontWeight: "var(--font-weight-semibold)" }}>
                Invio per cercare "{searchText}" in tutti i campi estratti (container, merce, indirizzi, P.IVA...)
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", flexShrink: 0 }}>
          {hasAnyFilter && (
            <button className="btn btn-ghost btn-sm" title="Pulisci tutti i filtri"
              onClick={() => {
                setFilter(Object.fromEntries(FILTER_KEYS.map((k) => [k, null])));
                setSearchText("");
              }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-danger)" }}>filter_list_off</span>
            </button>
          )}
          {checkMsg && <span className="text-xs" style={{ color: "var(--indaco-brand-primary)" }}>{checkMsg}</span>}
          <button className="btn btn-outline btn-sm" onClick={handleCheckNew} disabled={checking}
            title="Controlla subito nuove email nella casella BSC (forza aggiornamento)"
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {checking
              ? <span className="spinner" style={{ width: 14, height: 14 }} />
              : <span className="material-symbols-outlined" style={{ fontSize: 16 }}>mark_email_unread</span>}
            <span>Forza aggiornamento</span>
          </button>
          <span className="text-muted text-xs">{secondsAgo < 5 ? "aggiornato ora" : `${secondsAgo}s fa`}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => { load(); loadCounts(); }} title="Aggiorna">
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>refresh</span>
          </button>
        </div>
      </div>

      {/* Top bar: status pills (con totale + badge non-lette) + date range filter.
          Contatore = totale pratiche nello stato; badge rosso = NON lette in
          quella sezione (stato condiviso di team, dal count endpoint / polling 30s). */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-3)", flexWrap: "wrap", gap: "var(--space-2)" }}>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "center" }}>
          {STATUS_OPTIONS.map((opt) => {
            const c = counts?.[opt.value as keyof BscCount];
            const active = status === opt.value;
            return (
              <button key={opt.value} className={`btn btn-sm ${active ? "btn-primary" : "btn-outline"}`}
                onClick={() => setFilter({ status: opt.value })}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span>{opt.label}</span>
                {c && <span style={{ fontSize: 11, opacity: 0.7, fontVariantNumeric: "tabular-nums" }}>{c.total}</span>}
                {c && c.unread > 0 && (
                  <span className="badge badge-danger" title={`${c.unread} non lette`}
                    style={{ fontSize: 10, padding: "1px 6px", borderRadius: 10, lineHeight: 1.4 }}>
                    {c.unread}
                  </span>
                )}
                {/* Sempre presente su ogni pill (non solo quando ci sono non-lette):
                    disabilitata/sbiadita a 0 non-lette, altrimenti segna tutte lette
                    per quel tab in un colpo. */}
                {c && (
                  <span
                    className="material-symbols-outlined"
                    title={c.unread > 0 ? `Segna tutte come lette (${opt.label})` : "Nessuna pratica non letta in questo tab"}
                    onClick={(e) => { if (c.unread > 0) handleMarkAllRead(opt.value, e); else e.stopPropagation(); }}
                    style={{
                      fontSize: 15,
                      cursor: c.unread > 0 ? "pointer" : "default",
                      opacity: markingStatus === opt.value ? 0.4 : c.unread > 0 ? 0.85 : 0.25,
                    }}
                  >
                    done_all
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {/* Filtro range temporale (su created_at) */}
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--font-size-sm)" }}>
          <span className="text-muted">Dal:</span>
          <input
            type="date"
            className="form-input"
            value={dateFrom}
            onChange={(e) => setFilter({ date_from: e.target.value || null })}
            style={{ width: "auto", fontSize: "var(--font-size-sm)" }}
          />
          <span className="text-muted">al:</span>
          <input
            type="date"
            className="form-input"
            value={dateTo}
            onChange={(e) => setFilter({ date_to: e.target.value || null })}
            style={{ width: "auto", fontSize: "var(--font-size-sm)" }}
          />
          {(dateFrom || dateTo) && (
            <button
              className="btn btn-ghost btn-sm"
              title="Rimuovi filtro date"
              onClick={() => setFilter({ date_from: null, date_to: null })}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-danger)" }}>close</span>
            </button>
          )}
        </div>
      </div>

      {isLoading && !data && <div className="loading-center"><div className="spinner spinner-lg" /></div>}

      {error && (
        <div className="card" style={{ background: "var(--indaco-danger-light)", color: "var(--indaco-danger)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span> Errore: {error}
        </div>
      )}

      {/* Table */}
      {data && data.items.length > 0 && (
        <div className="card" style={{ padding: 0, opacity: isLoading ? 0.6 : 1, transition: "opacity 150ms" }}>
          <div className="table-container">
            <table style={{ tableLayout: "fixed", width: "100%", minWidth: 1080 }}>
              <thead>
                <tr>
                  {/* Ordine colonne concordato col cliente (2026-07-14):
                      Stato → Cliente → Paese destino → Dox → Completamento →
                      BL → Rotta → Exporter/Importer → Data invio.
                      Le colonne rimosse (N. Invoice, Nave/Viaggio, Extra CIF)
                      restano raggiungibili dalla barra di ricerca full-text. */}
                  {/* Stato: nessun sort/filtro qui — gia' controllato dalle pill sopra. */}
                  <th style={{ width: 40 }} />
                  {/* Cliente: chi ha aperto la pratica. Filtro su nome E indirizzo,
                      perche' la cella mostra l'uno o l'altro. */}
                  <ColHeader label="Cliente" sortKey="client" filterKey="client" filterType="text"
                    filterValue={fClient || undefined} style={{ width: 200, textAlign: "left" }}
                    sort={sort} openFilter={openFilter} setOpenFilter={setOpenFilter} setFilter={setFilter} setTextFilter={setTextFilter} />
                  {/* Origine del mittente: colonna sua, ordinabile e filtrabile
                      come le altre. Solo icona per non rubare larghezza — il
                      significato sta nel tooltip. */}
                  <ColHeader label="Origine" sortKey="origin" filterKey="origin" filterType="select"
                    options={CLIENT_SOURCE_OPTIONS} filterValue={fOrigin || undefined}
                    style={{ width: 70, textAlign: "center" }}
                    sort={sort} openFilter={openFilter} setOpenFilter={setOpenFilter} setFilter={setFilter} setTextFilter={setTextFilter} />
                  <ColHeader label="Paese" sortKey="country" filterKey="country" filterType="text"
                    filterValue={fCountry || undefined} style={{ width: 80, textAlign: "left" }}
                    sort={sort} openFilter={openFilter} setOpenFilter={setOpenFilter} setFilter={setFilter} setTextFilter={setTextFilter} />
                  <ColHeader label="Dox" sortKey="dox" filterKey="dox" filterType="select"
                    options={[
                      { value: "", label: "Tutte" },
                      { value: "complete", label: "BL + fattura" },
                      { value: "bl_only", label: "solo BL" },
                      { value: "invoice_only", label: "solo fattura" },
                      { value: "none", label: "nessun documento" },
                    ]}
                    filterValue={fDox || undefined} style={{ width: 90, textAlign: "center" }}
                    sort={sort} openFilter={openFilter} setOpenFilter={setOpenFilter} setFilter={setFilter} setTextFilter={setTextFilter} />
                  <ColHeader label="HANJA" sortKey="completeness" filterKey="hanja" filterType="select"
                    options={[
                      { value: "", label: "Tutte" },
                      { value: "complete", label: "completa (28/28)" },
                      { value: "minimal", label: "soglia minima raggiunta" },
                      { value: "not_minimal", label: "sotto la soglia minima" },
                    ]}
                    filterValue={fHanja || undefined} style={{ width: 110, textAlign: "center" }}
                    sort={sort} openFilter={openFilter} setOpenFilter={setOpenFilter} setFilter={setFilter} setTextFilter={setTextFilter} />
                  <ColHeader label="N. BL" sortKey="bl" filterKey="bl" filterType="text"
                    filterValue={fBl || undefined} style={{ width: 140 }}
                    sort={sort} openFilter={openFilter} setOpenFilter={setOpenFilter} setFilter={setFilter} setTextFilter={setTextFilter} />
                  <ColHeader label="Certificate ID" sortKey="cert" filterKey="cert" filterType="select"
                    options={[
                      { value: "", label: "Tutte" },
                      { value: "sent", label: "inviate a HANJA" },
                      { value: "not_sent", label: "non ancora inviate" },
                    ]}
                    filterValue={fCert || undefined} style={{ width: 120, textAlign: "left" }}
                    sort={sort} openFilter={openFilter} setOpenFilter={setOpenFilter} setFilter={setFilter} setTextFilter={setTextFilter} />
                  <ColHeader label="Rotta (POL → POD)" sortKey="route" filterKey="route" filterType="text"
                    filterValue={fRoute || undefined} style={{ width: 140, textAlign: "left" }}
                    sort={sort} openFilter={openFilter} setOpenFilter={setOpenFilter} setFilter={setFilter} setTextFilter={setTextFilter} />
                  <ColHeader label="Exporter / Importer" sortKey="party" filterKey="party" filterType="text"
                    filterValue={fParty || undefined} style={{ width: 220, textAlign: "left" }}
                    sort={sort} openFilter={openFilter} setOpenFilter={setOpenFilter} setFilter={setFilter} setTextFilter={setTextFilter} />
                  <ColHeader label="Data invio" sortKey="date" style={{ width: 100 }}
                    sort={sort} openFilter={openFilter} setOpenFilter={setOpenFilter} setFilter={setFilter} setTextFilter={setTextFilter} />
                  <th style={{ width: 40, textAlign: "center", fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-gray-600)" }}></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => {
                  const d = p.bsc_data;
                  const route = [d?.pol, d?.pod].filter(Boolean).join(" → ");
                  const parties = [d?.exporter_name, d?.importer_name].filter(Boolean).join(" / ");
                  // Cliente: chi scrive l'email a Frabemar (dalla firma). Nome in
                  // evidenza, email come riga secondaria; fallback "—" se assenti.
                  const clientName = d?.client_name || null;
                  const clientEmail = d?.client_email || null;
                  const clientSource = d?.client_email_source || null;
                  // Data mostrata = data di INVIO (sent_at). Se assente (pratica
                  // vecchia non ancora riprocessata) si ricade su created_at
                  // (elaborazione), segnalato con "*" + tooltip esplicito.
                  const dateIso = p.sent_at || p.created_at;
                  const dateIsSent = !!p.sent_at;
                  // Non letta (stato condiviso di team) -> riga in grassetto,
                  // come una mail non letta. Torna peso normale una volta aperta.
                  const isUnread = p.letta === false;
                  return (
                    <tr key={p.id}
                      style={{
                        ...(p.id === lastVisitedId ? { background: "var(--indaco-brand-primary-light)" } : {}),
                        ...(isUnread ? { fontWeight: "var(--font-weight-semibold)" } : {}),
                      }}
                      onClick={() => { setLastVisitedId(p.id); localStorage.setItem("lastVisitedBscId", p.id); goToPractice(p.id); }}>
                      {/* Stato */}
                      <td style={{ textAlign: "center", overflow: "hidden" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                          {getStatusIcon(p.status)}
                          {/* Chi tiene la pratica in carico (2026-07-28) — mancava
                              del tutto su BSC: ora e' visibile come sugli altri flow. */}
                          <LockChip lockedBy={p.locked_by} lockedByName={p.locked_by_name} lockedAt={p.locked_at} />
                        </span>
                      </td>
                      {/* Cliente (nome + email dalla firma email) */}
                      <td style={{ overflow: "hidden" }} title={[clientName, clientEmail].filter(Boolean).join(" · ")}>
                        {(clientName || clientEmail) ? (
                          <span style={{ display: "block" }}>
                            <span className="truncate" style={{ display: "block", fontSize: "var(--font-size-sm)", fontWeight: clientName ? "var(--font-weight-medium)" : "normal" }}>
                              {clientName || clientEmail}
                            </span>
                            {clientName && clientEmail && (
                              <span className="truncate text-muted" style={{ display: "block", fontSize: 11 }}>{clientEmail}</span>
                            )}
                          </span>
                        ) : <span className="text-muted">—</span>}
                      </td>
                      {/* Origine */}
                      <td style={{ textAlign: "center" }}>
                        <ClientSourceChip source={clientSource} iconOnly />
                      </td>
                      {/* Paese DESTINAZIONE RISOLTO (Phase 1) — priorita' BL
                          pod_country > importer_country. Il valore viene gia'
                          risolto dal BE (resolved_country / resolved_country_iso2),
                          quindi il FE non ri-deriva la priorita'. Badge blu quando
                          deriva dal BL (fonte autorevole per la destinazione),
                          neutro quando deriva dalla fattura (fallback). Icona
                          warning se country_anomaly (paese non africano, mercato
                          atteso di Frabemar). Tooltip mostra fonte + valore
                          originale per sicurezza operativa. */}
                      <td style={{ overflow: "hidden" }}>
                        {(() => {
                          // Fallback allineato al banner del dettaglio e alla
                          // priorità BE: BL (pod_country) > fattura (importer_country).
                          const resolved = p.resolved_country
                            ?? d?.pod_country ?? d?.importer_country ?? null;
                          const iso2 = p.resolved_country_iso2
                            ?? toCountryIso2(resolved);
                          const src = p.resolved_country_source;
                          const anom = p.country_anomaly;
                          if (!resolved) {
                            return <span className="text-muted" style={{ fontSize: "var(--font-size-xs)" }}>—</span>;
                          }
                          const tipParts: string[] = [];
                          if (src === "bl") tipParts.push("Fonte: BL (pod_country)");
                          else if (src === "invoice") tipParts.push("Fonte: fattura (importer_country)");
                          tipParts.push(`Originale: ${resolved}`);
                          if (anom) tipParts.push("⚠ Paese non africano — verificare");
                          const badgeClass = src === "bl" ? "badge-info" : "badge-neutral";
                          return (
                            <span style={{ display: "inline-flex", gap: 3, alignItems: "center" }}>
                              <span className={`badge ${anom ? "badge-warning" : badgeClass}`}
                                style={{ fontSize: 10, padding: "2px 6px", whiteSpace: "nowrap" }}
                                title={tipParts.join(" — ")}>
                                {iso2 ?? resolved}
                              </span>
                              {anom && (
                                <span className="material-symbols-outlined"
                                  style={{ fontSize: 14, color: "var(--indaco-warning)" }}
                                  title="Paese di destinazione non africano — verificare">
                                  warning
                                </span>
                              )}
                            </span>
                          );
                        })()}
                      </td>
                      {/* Documenti */}
                      <td style={{ textAlign: "center" }}>
                        <span style={{ display: "inline-flex", gap: 2, alignItems: "center" }}>
                          <span className={`badge ${p.has_bl ? "badge-info" : "badge-danger"}`} style={{ fontSize: 10, padding: "2px 5px" }} title={p.has_bl ? "Bill of Lading presente" : "BL mancante"}>BL</span>
                          <span className={`badge ${p.has_invoice ? "badge-info" : "badge-danger"}`} style={{ fontSize: 10, padding: "2px 5px" }} title={p.has_invoice ? "Fattura commerciale presente" : "Invoice mancante"}>INV</span>
                        </span>
                      </td>
                      {/* HANJA: minimo (bl_number) + completo (28 campi, incl. trasporto) — indicatore primario, Fase D 2026-07-23 */}
                      <td style={{ textAlign: "center" }}>
                        <HanjaBadges hanja={p.llm_metadata?.hanja} />
                      </td>
                      {/* BL */}
                      <td style={{ overflow: "hidden" }} title={d?.bl_number ?? ""}>
                        <code className="truncate" style={{ display: "block", fontFamily: "monospace", fontSize: "var(--font-size-sm)" }}>
                          {d?.bl_number || "—"}
                        </code>
                      </td>
                      {/* Certificate ID HANJA — vuoto finché la pratica non è
                          stata inviata (oggi tutte: endpoint non disponibile). */}
                      <td style={{ overflow: "hidden" }} title={p.hanja_certificate_id ?? "Non ancora inviato a HANJA"}>
                        {p.hanja_certificate_id ? (
                          <code className="truncate" style={{ display: "block", fontFamily: "monospace", fontSize: "var(--font-size-sm)" }}>
                            {p.hanja_certificate_id}
                          </code>
                        ) : (
                          <span className="text-muted text-xs">—</span>
                        )}
                      </td>
                      {/* Rotte */}
                      <td style={{ overflow: "hidden" }} title={route}>
                        <span className="truncate" style={{ display: "block", fontSize: "var(--font-size-sm)" }}>
                          {route || "—"}
                        </span>
                      </td>
                      {/* Parties (Exporter / Importer) */}
                      <td style={{ overflow: "hidden" }} title={parties}>
                        <span className="truncate" style={{ display: "block", fontSize: "var(--font-size-sm)" }}>
                          {parties || "—"}
                        </span>
                      </td>
                      {/* Data invio — sent_at; se assente, created_at (elaborazione)
                          come stima, marcata con "*" e tooltip esplicito. */}
                      <td style={{ whiteSpace: "nowrap", overflow: "hidden" }} className="text-sm">
                        <span title={dateIsSent
                          ? `Data di invio dell'email: ${formatDateFull(dateIso)}`
                          : `Data di invio non disponibile — mostrata la data di elaborazione: ${formatDateFull(dateIso)}`}>
                          {formatDate(dateIso)}
                          {!dateIsSent && <span className="text-muted" style={{ marginLeft: 3 }}>*</span>}
                        </span>
                      </td>
                      {/* Azioni */}
                      <td style={{ textAlign: "center" }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-400)" }} title="Apri dettaglio">open_in_new</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && data.items.length === 0 && !isLoading && (
        <div className="card text-center text-muted" style={{ padding: "var(--space-10)" }}>Nessuna pratica trovata</div>
      )}

      {/* Pagination */}
      {data && data.total > 0 && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "var(--space-3)", gap: "var(--space-2)" }}>
          {totalPages > 1 && (
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setFilter({ page: page === 2 ? null : String(page - 1) })}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_left</span>
              </button>
              {(() => {
                const pages: (number | "...")[] = [];
                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  pages.push(1);
                  if (page > 3) pages.push("...");
                  const start = Math.max(2, page - 1);
                  const end = Math.min(totalPages - 1, page + 1);
                  for (let i = start; i <= end; i++) pages.push(i);
                  if (page < totalPages - 2) pages.push("...");
                  pages.push(totalPages);
                }
                return pages.map((p, i) =>
                  p === "..." ? <span key={`e${i}`} className="text-muted" style={{ padding: "0 4px" }}>…</span>
                    : <button key={p} className={page === p ? "active" : ""}
                        onClick={() => setFilter({ page: p === 1 ? null : String(p) })}>{p}</button>
                );
              })()}
              <button disabled={page >= totalPages} onClick={() => setFilter({ page: String(page + 1) })}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_right</span>
              </button>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--font-size-sm)" }}>
            <span className="text-muted">Mostra</span>
            <select className="form-input" value={pageSize}
              style={{ width: 70, padding: "2px 6px", fontSize: "var(--font-size-sm)" }}
              onChange={(e) => setFilter({ size: e.target.value === String(DEFAULT_PAGE_SIZE) ? null : e.target.value, page: null })}>
              {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="text-muted">di {data.total}</span>
          </div>
        </div>
      )}
    </div>
  );
}
